import { Injectable } from '@angular/core';
import {
    AuthenticationResponseJSON,
    browserSupportsWebAuthn,
    PublicKeyCredentialCreationOptionsJSON,
    PublicKeyCredentialRequestOptionsJSON,
    RegistrationResponseJSON,
    startAuthentication,
    startRegistration,
    WebAuthnError,
} from '@simplewebauthn/browser';
import { from, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

/** Why a browser ceremony did not produce a response, in words a user can act on. */
export class PasskeyCeremonyError extends Error
{
    constructor(message: string, readonly cancelled: boolean) {
        super(message);
    }
}

/**
 * SD-9396: the browser half of passkeys.
 *
 * Thin by design. The server builds the WebAuthn options and verifies the result; this only hands
 * the options to the OS prompt and returns its answer, as standard WebAuthn JSON, so it does not
 * change if core swaps the provider behind the API.
 *
 * A cancelled or timed-out prompt (NotAllowedError) gets a neutral message, never a fallback: the
 * caller shows it and lets the user choose another method (no silent downgrade, OWASP MFA CS).
 */
@Injectable({providedIn: 'root'})
export class PasskeyService
{
    static readonly CANCELLED_MESSAGE = 'The passkey request was cancelled or timed out. You can try again or choose another method.';

    static readonly ALREADY_REGISTERED_MESSAGE = 'This device already has a passkey for your account.';

    static readonly FAILED_MESSAGE = 'We could not use a passkey on this device. You can try again or choose another method.';

    /** The browser library, behind one seam so specs can stand in for the OS prompt. */
    static readonly webauthn = {startRegistration, startAuthentication, browserSupportsWebAuthn};

    isSupported(): boolean {
        return PasskeyService.webauthn.browserSupportsWebAuthn();
    }

    register(options: PublicKeyCredentialCreationOptionsJSON): Observable<RegistrationResponseJSON> {
        return from(PasskeyService.webauthn.startRegistration({optionsJSON: options})).pipe(catchError((error) => throwError(() => this.toCeremonyError(error))));
    }

    authenticate(options: PublicKeyCredentialRequestOptionsJSON): Observable<AuthenticationResponseJSON> {
        return from(PasskeyService.webauthn.startAuthentication({optionsJSON: options})).pipe(catchError((error) => throwError(() => this.toCeremonyError(error))));
    }

    private toCeremonyError(error: unknown): PasskeyCeremonyError {
        if (error instanceof WebAuthnError && error.code === 'ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED') {
            return new PasskeyCeremonyError(PasskeyService.ALREADY_REGISTERED_MESSAGE, false);
        }
        if (error instanceof Error && (error.name === 'NotAllowedError' || error.name === 'AbortError')) {
            return new PasskeyCeremonyError(PasskeyService.CANCELLED_MESSAGE, true);
        }

        return new PasskeyCeremonyError(PasskeyService.FAILED_MESSAGE, false);
    }
}
